import { useEffect, useState, type ReactNode } from "react";
import { Save, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { useMe } from "@/hooks/useMe";
import {
  useStrategyAssumptions,
  useUpdateStrategyAssumptions,
  type StrategyAssumptions,
} from "@/hooks/useStrategy";

export function SettingsPage() {
  const me = useMe();
  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Profile, assumptions, integrations"
        description="Edit the inputs that drive every projection. Marginal tax rates are NOT typed here anymore — they're computed by the tax engine from your real intake."
      />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ProfileCard />
        <AssumptionsCard />
      </div>
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Identity
          </CardTitle>
          <CardDescription>Authenticated session for this device.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-muted-foreground">
          <div>
            Signed in as: <span className="text-foreground">{me.data?.displayName ?? me.data?.email ?? "—"}</span>
          </div>
          <div>Authenticated: {String(me.data?.isAuthenticated ?? false)}</div>
          {me.data?.role === undefined && (
            <Badge tone="warning" className="mt-2">
              Dev anonymous: API not bound to Entra in this environment.
            </Badge>
          )}
          <div>Role: {me.data?.role ?? "—"}</div>
          <div>Tenant: {me.data?.tenantId ?? "—"}</div>
        </CardContent>
      </Card>
    </>
  );
}

function ProfileCard() {
  const me = useMe();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>From Entra ID — not editable here.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Name" value={me.data?.displayName ?? "—"} />
        <Row label="Email" value={me.data?.email ?? "—"} />
        <Row label="User ID" value={<span className="font-mono text-xs">{me.data?.userId ?? "—"}</span>} />
        <Row label="Tenant" value={<span className="font-mono text-xs">{me.data?.tenantId ?? "—"}</span>} />
      </CardContent>
    </Card>
  );
}

/**
 * Strategy assumptions = ONLY user-supplied planning inputs (rate, hours, etc).
 * No marginal-rate sliders — those are computed from real tax intake. To shape
 * the projection, edit Tax Intake instead.
 */
function AssumptionsCard() {
  const assumptions = useStrategyAssumptions();
  const update = useUpdateStrategyAssumptions();
  const [draft, setDraft] = useState<StrategyAssumptions | null>(null);

  useEffect(() => {
    if (assumptions.data && !draft) setDraft(assumptions.data);
  }, [assumptions.data, draft]);

  if (assumptions.isLoading || !draft) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Strategy assumptions</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const num = (s: string) => (s === "" ? null : Number(s));
  const dateStr = (s: string) => (s === "" ? null : s);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Strategy assumptions
            </CardTitle>
            <CardDescription>
              Drives revenue projection + Solo 401(k) sizing. Tax rates come from{" "}
              <Link to="/tax-intake" className="underline">
                Tax Intake
              </Link>
              .
            </CardDescription>
          </div>
          <Button
            disabled={update.isPending}
            onClick={async () => {
              await update.mutateAsync(draft);
            }}
          >
            <Save className="h-4 w-4" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Hourly rate ($)" hint="Drives projected annual revenue.">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.hourlyRate ?? ""}
            onChange={(e) => setDraft({ ...draft, hourlyRate: num(e.target.value) })}
          />
        </Field>
        <Field label="Hours / week">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.hoursPerWeek ?? ""}
            onChange={(e) => setDraft({ ...draft, hoursPerWeek: num(e.target.value) })}
          />
        </Field>
        <Field
          label="Contract start date"
          hint="When billing begins. We pro-rate revenue inside the projection year from here."
        >
          <Input
            type="date"
            max="2099-12-31"
            value={draft.contractStartDate ?? ""}
            onChange={(e) => setDraft({ ...draft, contractStartDate: dateStr(e.target.value) })}
          />
        </Field>
        <Field
          label="Contract end date (optional)"
          hint="Leave blank for open-ended — we'll assume it runs through Dec 31."
        >
          <Input
            type="date"
            min={draft.contractStartDate ?? undefined}
            value={draft.contractEndDate ?? ""}
            onChange={(e) => setDraft({ ...draft, contractEndDate: dateStr(e.target.value) })}
          />
        </Field>
        <Field label="Projected annual overhead ($)">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.projectedAnnualOverhead ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, projectedAnnualOverhead: num(e.target.value) })
            }
          />
        </Field>
        <Field label="Solo 401(k) annual target ($)" hint="Combined employee + employer.">
          <Input
            type="number"
            inputMode="decimal"
            value={draft.targetSolo401kAnnual ?? ""}
            onChange={(e) => setDraft({ ...draft, targetSolo401kAnnual: num(e.target.value) })}
          />
        </Field>
        <Field label="Mega Backdoor Roth available?">
          <Select
            value={draft.megaBackdoorRothAvailable ? "yes" : "no"}
            onChange={(e) =>
              setDraft({ ...draft, megaBackdoorRothAvailable: e.target.value === "yes" })
            }
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </Field>
        <Field label="HSA eligible?">
          <Select
            value={draft.hsaEligible ? "yes" : "no"}
            onChange={(e) => setDraft({ ...draft, hsaEligible: e.target.value === "yes" })}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </Field>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1.5 last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
